import { Clock, Truck, Users, ShieldCheck, MapPin, Wrench } from "lucide-react";

/*
 * LocationWhyUs — Section 6/10 for /garage-door-repair-south-sydney/
 * MASTER TEMPLATE — duplicate for other locations, change only the LOC block.
 * Paste this ENTIRE file into src/pages/GarageDoorRepairSouthSydney/sections/LocationWhyUs.tsx
 */

// ╔══════════════════════════════════════════════════════════════════╗
// ║  LOC CONFIG — change ONLY this block when duplicating          ║
// ╚══════════════════════════════════════════════════════════════════╝
const LOC = {
  name: "South Sydney",
  h2Span: "South Sydney",
  subtitle: "We\'re practically neighbours. Bass Hill to Hurstville is 15 minutes — that\'s why South Sydney gets our fastest response.",
  reasons: [{
    icon: "Clock",
    title: "Fastest Response — Our Backyard",
    desc: "15–20 minutes from our base to most South Sydney suburbs. Often the first job of the day."
  }, {
    icon: "Truck",
    title: "Fully Stocked Van",
    desc: "Springs, cables, motors, remotes and panels — all on board. One visit, one fix."
  }, {
    icon: "Users",
    title: "Family-Run Since 2009",
    desc: "No call centres, no subcontractors. You talk to us, we show up, we do the work."
  }, {
    icon: "MapPin",
    title: "We Know South Sydney",
    desc: "Unit blocks in Mascot. Waterfront homes in Sans Souci. Federation houses in Hurstville. We\'ve done them all."
  }, {
    icon: "ShieldCheck",
    title: "1-Year Labour Warranty",
    desc: "Every repair, every install, every suburb — backed by our 1-year labour warranty."
  }, {
    icon: "Wrench",
    title: "All Brands, All Door Types",
    desc: "B&D, Merlin, ATA, Chamberlain, Steel-Line, Gliderol — sectional, roller, tilt, panel-lift."
  }]
};
// ╚══════════════════════════════════════════════════════════════════╝

const ICONS: Record<string, any> = {
  Clock,
  Truck,
  Users,
  ShieldCheck,
  MapPin,
  Wrench
};
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM2 = "#F0EBE0";
const INK = "#1A1F2E";
const INK2 = "#5A6478";
const NAVY = "#081438";
export default function LocationWhyUs() {
  return <section id="location-why-us" style={{
    background: CREAM,
    position: "relative",
    overflow: "hidden"
  }}>

      {/* Wave top — DUPLICATE from LocationProcess bottom */}
      <div style={{
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,60 C360,0 1080,0 1440,60 L1440,0 L0,0 Z" fill={NAVY} />
        </svg>
      </div>

      {/* Paper grain */}
      <svg aria-hidden="true" style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      opacity: 0.04,
      pointerEvents: "none",
      zIndex: 1
    }}>
        <filter id="lwu-grain"><feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="4" stitchTiles="stitch" /><feColorMatrix type="saturate" values="0" /></filter>
        <rect width="100%" height="100%" filter="url(#lwu-grain)" />
      </svg>

      {/* Corner ornaments */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: "40px",
      left: "40px",
      width: "64px",
      height: "64px",
      borderTop: `2px solid ${GOLD}50`,
      borderLeft: `2px solid ${GOLD}50`,
      zIndex: 1
    }} />
      <div aria-hidden="true" style={{
      position: "absolute",
      bottom: "40px",
      right: "40px",
      width: "64px",
      height: "64px",
      borderBottom: `2px solid ${GOLD}50`,
      borderRight: `2px solid ${GOLD}50`,
      zIndex: 1
    }} />

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: "1280px",
      margin: "0 auto",
      padding: "clamp(56px,7vw,88px) clamp(20px,5vw,48px)"
    }}>

        {/* Header */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "48px",
        marginBottom: "52px"
      }} className="lg:!grid-cols-[2fr_3fr]">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "20px"
          }}>
              <div style={{
              width: "32px",
              height: "2px",
              background: GOLD
            }} />
              <span style={{
              fontFamily: "Manrope,sans-serif",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: GOLD_DARK
            }}>
                Why Gold Garage Door
              </span>
            </div>
            <h2 style={{
            fontFamily: "Plus Jakarta Sans,sans-serif",
            fontWeight: 800,
            fontSize: "clamp(26px,3.5vw,42px)",
            lineHeight: 1.1,
            color: INK
          }}>
              Why {LOC.name} Trusts<br />
              <span style={{
              color: GOLD_DARK
            }}>Gold Garage Door</span>
            </h2>
          </div>
          <div style={{
          display: "flex",
          alignItems: "end"
        }}>
            <p style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "clamp(14px,1.5vw,16px)",
            color: INK2,
            lineHeight: 1.8
          }}>
              {LOC.subtitle}
            </p>
          </div>
        </div>

        {/* 6 reason cards with gold left accent */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
        gap: "20px"
      }}>
          {LOC.reasons.map(({
          icon,
          title,
          desc
        }, i) => {
          const Icon = ICONS[icon];
          return <div key={title} style={{
            background: "#FFFFFF",
            borderRadius: "14px",
            padding: "28px",
            paddingLeft: "32px",
            position: "relative",
            boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
            borderLeft: `4px solid ${GOLD}`,
            overflow: "hidden"
          }}>
                {/* Number watermark */}
                <div aria-hidden="true" style={{
              position: "absolute",
              top: "12px",
              right: "16px",
              fontFamily: "Plus Jakarta Sans,sans-serif",
              fontWeight: 900,
              fontSize: "48px",
              color: `${GOLD}12`,
              lineHeight: 1,
              userSelect: "none"
            }}>
                  0{i + 1}
                </div>

                {/** @preserve lucide-do-not-replace */}
                <div data-icon-container="lucide-do-not-replace" style={{
              width: "44px",
              height: "44px",
              borderRadius: "10px",
              background: `${GOLD}12`,
              border: `1px solid ${GOLD}25`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "16px"
            }}>
                  {Icon && <Icon size={22} color={GOLD_DARK} strokeWidth={1.8} />}
                </div>

                <h3 style={{
              fontFamily: "Plus Jakarta Sans,sans-serif",
              fontWeight: 700,
              fontSize: "16px",
              color: INK,
              lineHeight: 1.25,
              marginBottom: "8px",
              paddingRight: "40px"
            }}>
                  {title}
                </h3>
                <p style={{
              fontFamily: "Manrope,sans-serif",
              fontSize: "13px",
              color: INK2,
              lineHeight: 1.7
            }}>
                  {desc}
                </p>
              </div>;
        })}
        </div>

        {/* Trust strip */}
        <div style={{
        marginTop: "40px",
        textAlign: "center",
        display: "flex",
        flexWrap: "wrap",
        gap: "20px",
        justifyContent: "center"
      }}>
          {["5.0★ Google Rating", "Family-run since 2009", "1-Year Labour Warranty", `${LOC.name} daily`].map(t => <div key={t} style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px"
        }}>
              <span style={{
            color: GOLD_DARK,
            fontSize: "14px",
            fontWeight: 700
          }}>✓</span>
              <span style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "13px",
            color: INK,
            fontWeight: 600
          }}>{t}</span>
            </div>)}
        </div>
      </div>

      {/* Wave bottom — DUPLICATE in LocationSuburbs top */}
      <div style={{
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,0 C360,60 1080,60 1440,0 L1440,60 L0,60 Z" fill={NAVY} />
        </svg>
      </div>
    </section>;
}